import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useFlowActions } from '../hooks/useFlowActions';
import useInitialization from '../hooks/useInitialization';
import { registerComponents } from '../services/components';
import { type FlowState, createFlowStore } from '../services/flowReducer';
import { type AppState, PageType } from '../types/models';
import type { ExecutionMode } from '../types/public/models';
import type { Options } from '../types/public/options';
import { emptyAppState } from '../utils/state';
import Loader from './Loader';
import Root, { type SharedRootProps } from './Root';

type MainProps = SharedRootProps & {
    tenantId: string;
    flowId?: string | null;
    stateId?: string | null;
    flowVersion?: string | null;
    selectedOutcomeId?: string | null;
    authenticationToken?: string | null;
    executionMode?: ExecutionMode;
    environmentId?: string | null;
    culture?: string | null;
};

const buildInitialFlowState = (props: MainProps): FlowState => ({
    base: {
        ...emptyAppState,
        flowId: props.flowId ?? null,
        flowVersion: props.flowVersion ?? null,
        options: (props.options ?? {}) as Options,
        stateId: props.stateId ?? '',
        tenantId: props.tenantId ?? null,
        selectedOutcomeId: props.selectedOutcomeId ?? null,
        authenticationToken: props.authenticationToken ?? null,
        executionMode: props.executionMode ?? null,
        environmentId: props.environmentId ?? null,
        featureFlags: props.options?.featureFlags ?? {},
        ...(props.culture ? { culture: props.culture } : {}),
    },
    modal: emptyAppState,
});

/**
 * The runtime entry point. Owns the flow store and switches between the base and
 * modal Roots, so the base page is shown behind the modal page.
 */
const Main = (props: MainProps) => {
    const [store] = useState(() => createFlowStore(buildInitialFlowState(props)));

    const flowState = useSyncExternalStore(store.subscribe, store.getState);
    const appState = flowState.base;
    const modalState = flowState.modal;

    const baseActions = useFlowActions('base', store);
    const modalActions = useFlowActions('modal', store);

    const mainRef = useRef<HTMLDivElement>(null);

    const updateState = useCallback(
        (newAppState: AppState) => {
            store.dispatch({
                type: 'SET_STATE',
                target: newAppState.pageType === PageType.Modal ? 'modal' : 'base',
                appState: newAppState,
            });
        },
        [store],
    );

    const setError = useCallback(
        (errorMessage: string) => {
            store.dispatch({ type: 'SET_ERROR', message: errorMessage });
        },
        [store],
    );

    const isModalOpen = modalState.pageType === PageType.Modal;

    registerComponents(props.options?.components ?? {});

    const navigations = appState.navigations
        ? appState.navigations.filter((nav) => !nav.progressTracker)
        : null;

    const isLeft =
        Array.isArray(navigations) &&
        navigations.length > 0 &&
        navigations[0].position.toLowerCase() === 'left';

    const mainClasses = classNames({
        flow: true,
        'nav-vertical': isLeft,
        'nav-horizontal': !isLeft,
    });

    useInitialization(appState, updateState, setError, props.executionMode);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        if (mainRef.current !== null && typeof mainRef.current.scrollTo === 'function') {
            mainRef.current.scrollTo(0, 0);
        }
    }, [appState.currentMapElementId]);

    return (
        <main className={mainClasses} ref={mainRef}>
            <Root {...props} appState={appState} actions={baseActions} isModalOpen={isModalOpen} />
            {isModalOpen && (
                <Root
                    {...props}
                    appState={modalState}
                    actions={modalActions}
                    isModalOpen={isModalOpen}
                    isModal={true}
                />
            )}
            <Loader isLoading={appState.isLoading} />
        </main>
    );
};

export default Main;
